import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader, Search, Workflow, X } from 'lucide-react';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { studioCatalogService, type StudioProject } from '../studioCatalogService';
import type { WorkflowSource } from '../governedMcpTypes';

const UNGROUPED = 'ungrouped';

function rowClass(isLight: boolean, selected: boolean): string {
  if (selected) {
    return isLight
      ? 'w-full text-left px-3 py-2.5 text-sm bg-indigo-50 text-indigo-700'
      : 'w-full text-left px-3 py-2.5 text-sm bg-indigo-500/15 text-indigo-200';
  }
  return isLight
    ? 'w-full text-left px-3 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50'
    : 'w-full text-left px-3 py-2.5 text-sm text-neutral-200 hover:bg-neutral-900';
}

function columnClass(isLight: boolean): string {
  return isLight
    ? 'rounded-xl border border-neutral-200 bg-white overflow-hidden min-h-0 h-52 md:h-full flex flex-col'
    : 'rounded-xl border border-neutral-800 bg-neutral-950 overflow-hidden min-h-0 h-52 md:h-full flex flex-col';
}

export default function WorkflowPickerModal({
  open,
  selectedId,
  isLight,
  onClose,
  onApply,
}: {
  open: boolean;
  selectedId: string;
  isLight: boolean;
  onClose: () => void;
  onApply: (workflow: WorkflowSource) => void;
}) {
  const [query, setQuery] = useState('');
  const [projects, setProjects] = useState<StudioProject[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupId, setGroupId] = useState('');
  const [draftId, setDraftId] = useState(selectedId);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDraftId(selectedId);
    setGroupId('');
  }, [open, selectedId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void studioCatalogService
      .load()
      .then((catalog) => {
        if (cancelled) return;
        setProjects(catalog.projects);
        setWorkflows(catalog.workflows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load Studio workflows');
        setProjects([]);
        setWorkflows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const groups = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    projects.forEach((project) => counts.set(project.id, { name: project.name, count: 0 }));
    workflows.forEach((item) => {
      const id = item.projectId || UNGROUPED;
      const current = counts.get(id) || { name: item.projectName || (id === UNGROUPED ? 'Ungrouped' : id), count: 0 };
      counts.set(id, { name: current.name, count: current.count + 1 });
    });
    if (!Array.from(counts.values()).some((item) => item.count > 0) && !counts.has(UNGROUPED)) {
      counts.set(UNGROUPED, { name: 'Ungrouped', count: 0 });
    }
    return Array.from(counts.entries())
      .map(([id, item]) => ({ id, name: item.name, count: item.count }))
      .sort((a, b) => {
        if (a.id === UNGROUPED) return 1;
        if (b.id === UNGROUPED) return -1;
        return a.name.localeCompare(b.name);
      });
  }, [projects, workflows]);

  useEffect(() => {
    if (!open || groupId || !groups.length) return;
    const selected = workflows.find((item) => item.id === selectedId);
    setGroupId(selected?.projectId || groups.find((group) => group.count > 0)?.id || groups[0].id);
  }, [groupId, groups, open, selectedId, workflows]);

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => {
      if (group.name.toLowerCase().includes(q)) return true;
      return workflows.some(
        (item) =>
          (item.projectId || UNGROUPED) === group.id &&
          `${item.name} ${item.code} ${item.description}`.toLowerCase().includes(q),
      );
    });
  }, [groups, query, workflows]);

  const groupWorkflows = workflows.filter((item) => (item.projectId || UNGROUPED) === groupId);
  const visibleWorkflows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groupWorkflows;
    return groupWorkflows.filter((item) => `${item.name} ${item.code} ${item.description}`.toLowerCase().includes(q));
  }, [groupWorkflows, query]);

  if (!open) return null;
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const panel = isLight ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-white';
  const rail = isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-950';
  const selected = workflows.find((item) => item.id === draftId);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close workflow picker" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-picker-title"
        className={`relative flex flex-col w-full max-w-4xl max-h-[min(40rem,calc(100dvh-2rem))] overflow-hidden rounded-2xl shadow-2xl ${panel}`}
      >
        <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b border-black/5">
          <div>
            <h3 id="workflow-picker-title" className="text-base font-semibold">Select a workflow</h3>
            <p className={`text-xs mt-0.5 ${muted}`}>Published Studio workflows only. Pick a project, then one workflow.</p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className={`shrink-0 px-5 py-4 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <div className="relative">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
            <input
              className={`${fieldClass(isLight)} pl-9`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter projects or workflows"
            />
          </div>
        </div>
        <div className={`shrink-0 px-5 py-2 text-xs ${rail}`}>
          <p className={muted}>{draftId ? selected?.name || draftId : 'No workflow selected'}</p>
        </div>
        <div className="grid md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] gap-3 p-5 min-h-0 flex-1 overflow-y-auto md:overflow-hidden">
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Projects</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              {loading ? (
                <p className={`flex items-center gap-2 px-3 py-6 text-sm ${muted}`}>
                  <Loader size={14} className="animate-spin" /> Loading projects…
                </p>
              ) : null}
              {error ? <p className="px-3 py-6 text-sm text-red-500">{error}</p> : null}
              {visibleGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={rowClass(isLight, group.id === groupId)}
                  onClick={() => setGroupId(group.id)}
                >
                  <span className="block truncate font-medium">{group.name}</span>
                  <span className={`block truncate text-[11px] ${muted}`}>{group.count} workflow{group.count === 1 ? '' : 's'}</span>
                </button>
              ))}
              {!loading && !error && !visibleGroups.length ? (
                <p className={`px-3 py-6 text-sm ${muted}`}>{workflows.length ? 'No projects match this filter.' : 'No published Studio workflows are available.'}</p>
              ) : null}
            </div>
          </section>
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Workflows</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              {visibleWorkflows.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={rowClass(isLight, item.id === draftId)}
                  onClick={() => setDraftId(item.id)}
                >
                  <span className="flex items-center gap-2.5">
                    <Workflow size={14} className="shrink-0" />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{item.name}</span>
                      <span className={`block truncate text-[11px] ${muted}`}>
                        {item.code || item.id}
                        {item.status ? ` · ${item.status}` : ''}
                        {item.description ? ` · ${item.description}` : ''}
                      </span>
                    </span>
                  </span>
                </button>
              ))}
              {!loading && !visibleWorkflows.length ? (
                <p className={`px-3 py-6 text-sm ${muted}`}>
                  {groupWorkflows.length ? 'No workflows match this filter.' : 'This project has no workflows.'}
                </p>
              ) : null}
            </div>
          </section>
        </div>
        <div className={`shrink-0 flex items-center justify-end gap-2 px-5 py-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              onApply(selected);
              logGovernedMcp('app', 'publish_workflow_picked', { sourceId: selected.id });
            }}
          >
            <Check size={14} /> Use workflow
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
